import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldAlert, Lock } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { PageHero, Section, Field, SerialSuccess } from "../components/PageKit";
import { usePageMeta } from "../lib/seo";

const CHANNELS = {
  safeguarding: {
    title: "Safeguarding", kicker: "Protection first",
    intro: "Clear, confidential routes to report concerns about the safety of children, vulnerable adults and anyone who comes into contact with the Foundation.",
    categories: ["Child safeguarding", "Vulnerable adult concern", "Staff misconduct", "Volunteer misconduct", "Abuse", "Exploitation", "Harassment", "Beneficiary protection"],
  },
  complaint: {
    title: "Complaints & Whistleblowing", kicker: "Speak up safely",
    intro: "Separate from normal contact. Report fraud, misuse of funds, unfair selection, procurement misconduct, political interference or data misuse — anonymously if you prefer.",
    categories: ["Fraud", "Bribery", "Misuse of funds", "Unfair selection", "Procurement misconduct", "Staff behaviour", "Political interference", "Data misuse", "Safeguarding concern", "Other complaint"],
  },
};

export default function Concerns({ channel = "complaint" }) {
  const cfg = CHANNELS[channel];
  usePageMeta(cfg.title, cfg.intro);
  const fraudLink = typeof window !== "undefined" && window.location.hash === "#fraud" && cfg.categories.includes("Fraud");
  const [f, setF] = useState({ category: fraudLink ? "Fraud" : cfg.categories[0], description: "", anonymous: true, reporter_name: "", reporter_contact: "", people_involved: "", when_occurred: "", consent_contact: false });
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF({ ...f, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const ch = channel === "complaint" && f.category !== "Other complaint" && ["Fraud", "Bribery", "Misuse of funds", "Procurement misconduct", "Political interference", "Data misuse"].includes(f.category) ? "whistleblowing" : channel;
      const { data } = await api.post("/concerns", { ...f, channel: ch });
      setRes(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={`${channel}-page`}>
      <PageHero kicker={cfg.kicker} title={cfg.title} text={cfg.intro} />
      <Section className="bg-[#f8f4e8]">
        {res ? (
          <SerialSuccess serial={res.serial} title="Report received" text="Thank you. Your report has been logged confidentially and will be reviewed by the designated officer. Keep this reference if you wish to follow up." testid="concern-success" />
        ) : (
          <div className="grid lg:grid-cols-5 gap-8">
            <form id="fraud" onSubmit={submit} className="lg:col-span-3 bg-white rounded-2xl p-8 border border-black/5 space-y-5" data-testid="concern-form">
              <Field label="What is this about?" value={f.category} onChange={set("category")} options={cfg.categories.map((c) => [c, c])} testid="concern-category" />
              <Field label="Describe what happened" required textarea value={f.description} onChange={set("description")} testid="concern-description" placeholder="Who, what, where and when. Do not include bank details or passwords." />
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="People or roles involved (optional)" value={f.people_involved} onChange={set("people_involved")} testid="concern-people" />
                <Field label="When did it occur? (optional)" value={f.when_occurred} onChange={set("when_occurred")} testid="concern-when" />
              </div>
              <label className="flex items-start gap-3 p-4 rounded-md bg-[#f8f4e8] text-sm"><input type="checkbox" checked={f.anonymous} onChange={(e) => setF({ ...f, anonymous: e.target.checked })} data-testid="concern-anonymous" className="mt-1" /> <span><strong>Report anonymously.</strong> We will not record your name or contact details.</span></label>
              {!f.anonymous && (
                <div className="grid sm:grid-cols-2 gap-4" data-testid="concern-identity">
                  <Field label="Your name" value={f.reporter_name} onChange={set("reporter_name")} testid="concern-name" />
                  <Field label="Phone or email" value={f.reporter_contact} onChange={set("reporter_contact")} testid="concern-contact" />
                  <label className="sm:col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={f.consent_contact} onChange={(e) => setF({ ...f, consent_contact: e.target.checked })} /> You may contact me about this report.</label>
                </div>
              )}
              <button type="submit" disabled={busy} className="dk-btn-primary w-full justify-center disabled:opacity-60" data-testid="concern-submit"><Lock size={16} /> {busy ? "Sending…" : "Submit confidential report"}</button>
            </form>
            <aside className="lg:col-span-2 space-y-5">
              <div className="bg-[#155e3f] text-white rounded-2xl p-7">
                <ShieldAlert className="text-[#d6aa43] mb-3" />
                <h3 className="font-serif text-xl font-bold mb-2">How reports are handled</h3>
                <ul className="text-sm text-white space-y-2 list-disc pl-4">
                  <li>Reviewed only by the designated {channel === "safeguarding" ? "Safeguarding Lead" : "Integrity Officer"} and trustees.</li>
                  <li>Recorded with an audit trail; identity kept confidential.</li>
                  <li>No retaliation against anyone who reports in good faith.</li>
                  <li>Immediate danger? Contact emergency services first, then <a href="tel:+2348063550963" className="text-[#d6aa43]">+234 806 355 0963</a>.</li>
                </ul>
              </div>
              <div className="bg-white rounded-2xl p-7 border border-black/5 text-sm text-[#0a0d12]/75">
                DKF will never request your password, PIN or payment in exchange for application approval. Anyone asking is not acting for the Foundation — <Link to="/complaints" className="underline">report it here</Link>.
              </div>
            </aside>
          </div>
        )}
      </Section>
    </div>
  );
}
